import React, { useState, useEffect, useCallback } from 'react';
import { ThreadMember, ThreadConsent } from '../types';
import { threadService, CONSENT_PURPOSES } from '../services/api';
import { formatDateTime } from '../utils/datetime';
import ScopeFilter, { ScopeSelection } from './ScopeFilter';

// Threads: conversations the platform has been told, or been asked to accept,
// are one interaction.
//
// The vCons page answers "what does THIS conversation belong to". This one
// answers the other direction — what interactions exist, and what is in them —
// which is the view you want after a transfer chain, when the individual
// segments are the least interesting way to look at what happened.
//
// Collapsed by default: a thread is a summary until someone wants the parts.

interface ThreadSummary {
  thread_id: string;
  members: number;
  first_at: string;
  last_at: string;
  kinds: string[];
  linked_by: string | null;
  linked_at: string;
  participants: { name: string | null; tel: string | null; uid: string | null }[];
}

const partyLabel = (p: { name: string | null; tel: string | null; uid: string | null }): string =>
  p.name || p.uid || p.tel || '—';

// A letter per distinct SIPREC session within one thread, in the order the
// sessions first appear. Group ids are 40-odd characters of SBC identifier and
// six of them in a column is unreadable; what a person actually needs is
// "which session is this, and which did it come from", which one letter
// answers. The real id stays in the tooltip.
export const sessionLetters = (members: ThreadMember[]): Record<string, string> => {
  const map: Record<string, string> = {};
  let n = 0;
  members.forEach(m => {
    if (m.group_id && !(m.group_id in map)) {
      map[m.group_id] = String.fromCharCode(65 + (n % 26)) + (n >= 26 ? String(Math.floor(n / 26)) : '');
      n++;
    }
  });
  return map;
};

// The letters, spelled out. A tooltip is the wrong home for an identifier:
// you cannot see it without knowing to hover, cannot compare two, and cannot
// easily copy one into a query. The legend states them once per thread, in
// full, selectable — and the letters in the table stay short because the ids
// are here rather than repeated on every row.
export const SessionLegend: React.FC<{ members: ThreadMember[] }> = ({ members }) => {
  const letters = sessionLetters(members);
  const entries = Object.entries(letters);
  if (entries.length === 0) {
    return null;
  }
  return (
    <div className="small" style={{ marginTop: '0.5rem' }}>
      <strong>SIPREC sessions in this thread</strong>
      <table className="sessions-table" style={{ marginTop: '0.3rem' }}>
        <thead>
          <tr><th>Session</th><th>Group ID</th><th>Segments</th></tr>
        </thead>
        <tbody>
          {entries.map(([groupId, letter]) => {
            const segs = members.filter(m => m.group_id === groupId);
            return (
              <tr key={groupId}>
                <td className="monospace">{letter}</td>
                <td className="monospace" style={{ userSelect: 'all', wordBreak: 'break-all' }}>
                  {groupId}
                </td>
                <td className="small">
                  {segs.length} ({segs.map(x => x.group_seq ?? '?').join(', ')})
                  {segs.some(x => x.by_action) && (
                    <> · {Array.from(new Set(segs.map(x => x.by_action).filter(Boolean))).join(', ')}</>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// How long a thread ran, in the units a person would use. A transfer chain is
// seconds apart and an email follow-up is days; "0h" for both tells you
// nothing.
const spanLabel = (first: string, last: string): string => {
  const ms = new Date(last).getTime() - new Date(first).getTime();
  if (!isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 90) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.round(h / 24)}d`;
};

const ThreadsPage: React.FC = () => {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<ScopeSelection>({ territory: '', domain: '' });

  // Expanded threads, and what has been fetched for each. Kept per thread so
  // opening a second does not close the first — comparing two threads is a
  // real thing to want after a transfer went somewhere unexpected.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [members, setMembers] = useState<Record<string, ThreadMember[]>>({});
  const [consent, setConsent] = useState<Record<string, Record<string, ThreadConsent>>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const fetchThreads = useCallback(async () => {
    setError(null);
    try {
      setThreads(await threadService.list(scope.territory, scope.domain) as ThreadSummary[]);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to fetch threads.');
    } finally {
      setLoading(false);
    }
  }, [scope.territory, scope.domain]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  const toggle = async (threadId: string) => {
    const next = new Set(expanded);
    if (next.has(threadId)) {
      next.delete(threadId);
      setExpanded(next);
      return;
    }
    next.add(threadId);
    setExpanded(next);
    if (!members[threadId]) {
      try {
        const t = await threadService.byId(threadId);
        setMembers(prev => ({ ...prev, [threadId]: t }));
      } catch {
        setError('Failed to load that thread.');
      }
    }
  };

  const askConsent = async (threadId: string, purpose: string) => {
    try {
      const c = await threadService.consent(threadId, purpose);
      setConsent(prev => ({ ...prev, [threadId]: { ...(prev[threadId] || {}), [purpose]: c } }));
    } catch {
      setError('Failed to evaluate thread consent.');
    }
  };

  const unlink = async (threadId: string, uuid: string) => {
    setBusy(threadId);
    setError(null);
    try {
      await threadService.unlink(threadId, uuid);
      const t = await threadService.byId(threadId);
      setMembers(prev => ({ ...prev, [threadId]: t }));
      // The count in the summary row is now wrong, and a thread that lost its
      // last member is gone entirely.
      await fetchThreads();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to unlink.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="loading">Loading threads...</div>;

  return (
    <div className="table-wrapper">
      <div className="table-header">
        <h2>Threads</h2>
        <span className="small">
          {threads.length} interaction{threads.length === 1 ? '' : 's'}
        </span>
      </div>

      <p className="small" style={{ maxWidth: '58rem' }}>
        A thread is several conversations that are one interaction — the segments of a
        transferred call, or a call and the email that settled it. Segments the recording
        client itself identified (one SIPREC session, or a supervised transfer&rsquo;s
        consultation leg) are linked automatically; anything inferred from shared
        participants is linked only when someone confirms it on the conversation itself.
      </p>

      <ScopeFilter value={scope} onChange={setScope} />

      {error && <div className="error">{error}</div>}

      {threads.length === 0 ? (
        <p className="small">
          No threads yet. Open a conversation on the vCons page and use Thread to link it
          with others, or run <span className="monospace">thread_autolink.php</span> to link
          the transfer segments the recorder already identified.
        </p>
      ) : (
        <div className="table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th></th>
                <th>Thread</th>
                <th>Parts</th>
                <th>Started</th>
                <th>Span</th>
                <th>Kinds</th>
                <th>Participants</th>
                <th>Linked by</th>
              </tr>
            </thead>
            <tbody>
              {threads.map(t => (
                <React.Fragment key={t.thread_id}>
                  <tr>
                    <td>
                      <button type="button" onClick={() => toggle(t.thread_id)}
                        title={expanded.has(t.thread_id) ? 'Collapse' : 'Show the conversations in this thread'}>
                        {expanded.has(t.thread_id) ? '▾' : '▸'}
                      </button>
                    </td>
                    <td className="monospace small">{t.thread_id}</td>
                    <td>{t.members}</td>
                    <td className="small">{formatDateTime(t.first_at)}</td>
                    <td className="small">{spanLabel(t.first_at, t.last_at)}</td>
                    <td className="small">{t.kinds.join(', ')}</td>
                    <td className="small">
                      {t.participants.map(partyLabel).join(', ')}
                    </td>
                    <td className="small">{t.linked_by || '—'}</td>
                  </tr>

                  {expanded.has(t.thread_id) && (
                    <tr>
                      <td colSpan={8} style={{ paddingLeft: '28px' }}>
                        {!members[t.thread_id] ? (
                          <span className="small">Loading…</span>
                        ) : (
                          <>
                            <table className="sessions-table">
                              <thead>
                                <tr>
                                  <th>When</th><th>Kind</th><th>Parties</th>
                                  <th title="Which SIPREC recording session this segment belongs to">Session</th>
                                  <th title="Its position within that session (group_seq)">Seq</th>
                                  <th title="What the SBC called the state change (byAction)">Action</th>
                                  <th title="The session this one was transferred from (xferredGroupID)">From</th>
                                  <th>Scope</th><th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const letters = sessionLetters(members[t.thread_id]);
                                  return members[t.thread_id].map(m => (
                                  <tr key={m.vcon_uuid}>
                                    <td className="small">{formatDateTime(m.started_at)}</td>
                                    <td className="small">{m.kind}</td>
                                    <td className="small">
                                      {m.parties.map(p => p.name || p.tel || '—').join(' → ')}
                                      <div className="monospace" style={{ opacity: 0.7 }}>{m.vcon_uuid}</div>
                                    </td>
                                    <td className="small monospace" title={m.group_id || 'no SIPREC session'}>
                                      {m.group_id ? letters[m.group_id] : '—'}
                                    </td>
                                    <td className="small monospace">{m.group_seq ?? '—'}</td>
                                    <td className="small">{m.by_action || '—'}</td>
                                    <td className="small monospace"
                                      title={m.xferred_group_id || 'not a transfer'}>
                                      {m.xferred_group_id
                                        ? (letters[m.xferred_group_id] || m.xferred_group_id.slice(0, 8))
                                        : '—'}
                                    </td>
                                    <td className="small">{m.territory}{m.domain ? ` / ${m.domain}` : ''}</td>
                                    <td>
                                      <button type="button" disabled={busy === t.thread_id}
                                        title="Remove this conversation from the thread"
                                        onClick={() => unlink(t.thread_id, m.vcon_uuid)}>
                                        Unlink
                                      </button>
                                    </td>
                                  </tr>
                                  ));
                                })()}
                              </tbody>
                            </table>

                            {/* Asked for, never volunteered: a thread-wide answer is
                                the most restrictive member's, so showing it beside the
                                members would invite reading it as each member's own. */}
                            <div className="small" style={{ marginTop: '0.6rem', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span title="A thread-wide answer takes the most restrictive member's — one conversation whose parties declined is decisive">
                                May the whole thread be used for:
                              </span>
                              {CONSENT_PURPOSES.map(p => (
                                <button key={p} type="button" onClick={() => askConsent(t.thread_id, p)}>
                                  {p}
                                </button>
                              ))}
                            </div>
                            <SessionLegend members={members[t.thread_id]} />
                            <p className="small" style={{ opacity: 0.8, marginTop: '0.4rem' }}>
                              Letters are assigned per thread in the order the sessions appear. A
                              supervised transfer reads as its consultation being a session of its
                              own, named in From; a blind transfer points From at its own session.
                            </p>
                            {consent[t.thread_id] && Object.entries(consent[t.thread_id]).map(([purpose, c]) => (
                              <div key={purpose} className="small" style={{ marginTop: '0.3rem' }}>
                                <span className={c.allowed ? 'status status-completed' : 'status status-failed'}>
                                  {c.allowed ? 'permitted' : 'not permitted'}
                                </span>{' '}
                                for <span className="monospace">{purpose}</span>
                                {c.blocked_by.length > 0 && (
                                  <> — blocked by{' '}
                                    {c.blocked_by.map(b => (
                                      <span key={b.uuid} className="monospace" title={b.uuid}>
                                        {b.uuid.slice(0, 8)}… ({b.status}){' '}
                                      </span>
                                    ))}
                                  </>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ThreadsPage;
